import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Download, Loader2 } from 'lucide-react';

const API = '/api';

const REASON_META = {
  dnc_flagged: { title: 'Do-Not-Contact', color: 'red', hint: 'Flagged Do-Not-Contact. Send blocked at the send API.' },
  replied_historically: { title: 'Already replied', color: 'slate', hint: 'Replied to prior outreach — warm follow-up, not a cold pitch.' },
  meeting_logged: { title: 'Meeting logged', color: 'slate', hint: 'A meeting / call is already on record.' },
  commercialization_partner: { title: 'Commercialization partner', color: 'slate', hint: 'Already a distributor / commercial partner.' },
  my_company: { title: 'Your own portfolio', color: 'slate', hint: 'Your own biotech companies — blocked to prevent self-emails.' },
  client_already_in_touch: { title: 'Biotech in touch', color: 'slate', hint: 'The biotech is already in active discussion with these companies.' },
  not_buyer_eligible: { title: 'Handshake OFF', color: 'red', hint: 'Buyer handshake is OFF on these profiles — no email until toggled ON.' },
  manually_excluded: { title: 'Manually excluded', color: 'slate', hint: 'Added to this asset\u2019s Exclude List explicitly.' },
  noise_buyer: { title: 'Removed as non-buyer noise', color: 'slate', hint: 'CROs / diagnostics / consumer-goods — never licensing targets.' },
  company_stage_gate: { title: 'Company stage gate', color: 'indigo', hint: 'Org-wide stage gate (e.g. Phase 3+) excludes this asset\u2019s stage.' },
  rationale_gate_dropped: { title: 'Dropped at mapping — rationale not strong enough', color: 'orange', hint: 'The AI proposed these companies but the deterministic rationale gate rejected the rationale (thin / generic / cross-TA product anchor / unverified product). Reinstate with your own rationale if you disagree.' },
  handshake_pruned: { title: 'Not a qualified in-licenser', color: 'slate', hint: 'Biotechs from your database removed by the tightened rule: no declared In-Licensing interest, or therapeutic focus not a precise match.' },
};
const BORDER = { red: 'border-red-200 bg-red-50/50', indigo: 'border-indigo-200 bg-indigo-50/50', orange: 'border-orange-200 bg-orange-50/50', slate: 'border-slate-200 bg-white' };

const NeedsContactList = ({ items, exportMeta }) => {
  const [busy, setBusy] = useState(false);
  const pharma = items.filter(i => i.kind === 'Pharma').length;
  const exportXlsx = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ai/needs-contact-export`, { ...exportMeta, rows: items }, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = `Needs_BD_contact_${(exportMeta.asset_name || 'mapping').replace(/[^A-Za-z0-9]+/g, '_')}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); } finally { setBusy(false); }
  };
  return (
    <div className="p-2.5 rounded-md border border-orange-200 bg-orange-50/50" data-testid="skipped-group-no_bd_contact">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div>
          <span className="text-[11px] font-semibold text-slate-800">Needing a BD contact ({items.length})</span>
          <span className="text-[10px] text-slate-500 ml-1.5">{pharma} pharma · {items.length - pharma} biotech in-licensers</span>
        </div>
        <button type="button" onClick={exportXlsx} disabled={busy} className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50" data-testid="needs-contact-export-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Export to Excel
        </button>
      </div>
      <p className="text-[10px] text-slate-500 mb-1.5">Mapped and eligible, but nobody with an email is on file. Hand this list to your team to source contacts, then re-open outreach.</p>
      <ol className="max-h-64 overflow-y-auto divide-y divide-orange-100 rounded border border-orange-100 bg-white" data-testid="needs-contact-list">
        {items.map((it, idx) => (
          <li key={`${it.company_name}-${idx}`} className="flex items-center gap-2 px-2 py-1 text-[11px]" data-testid={`needs-contact-row-${idx}`}>
            <span className="w-5 text-right tabular-nums text-slate-400">{idx + 1}</span>
            <span className="font-medium text-slate-800 truncate" title={it.rationale || ''}>{it.company_name}</span>
            {it.country && <span className="text-[10px] text-slate-400 shrink-0">{it.country}</span>}
            <span className={`text-[9px] px-1 rounded shrink-0 ${it.kind === 'Pharma' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}`}>{it.kind === 'Pharma' ? 'Pharma' : 'Biotech in-licenser'}</span>
            {(it.tier || it.confidence) && <span className="text-[10px] text-slate-400 shrink-0 hidden sm:inline">{[it.tier, it.confidence].filter(Boolean).join(' · ')}</span>}
            <span className="flex-1" />
            {it.diag && <span className="text-[10px] text-slate-400 cursor-help" title={it.diag} data-testid={`skipped-diag-${it.company_name}`}>ⓘ</span>}
            {it.action_url && (
              <a href={it.action_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline text-[10px] font-medium shrink-0" data-testid={`needs-contact-action-${idx}`}>{it.action_label} →</a>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export const SkippedPanel = ({ skipped, exportMeta }) => {
  if (!skipped?.length) return null;
  const needs = skipped.filter(s => s.reason === 'no_bd_contact');
  const groups = skipped.filter(s => s.reason !== 'no_bd_contact').reduce((acc, s) => { (acc[s.reason] = acc[s.reason] || []).push(s); return acc; }, {});
  const blocked = skipped.length - needs.length;
  return (
    <div className="mt-2 mb-3 rounded-md border border-slate-200 bg-slate-50/60" data-testid="skipped-panel">
      <details>
        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-2 hover:bg-slate-100 rounded-md" data-testid="skipped-panel-summary">
          <AlertCircle className="w-3.5 h-3.5 text-slate-500" />
          Not in this outreach ({skipped.length})
          <span className="text-[10px] font-normal text-slate-500 ml-1">
            {needs.length > 0 && <>{needs.length} need a BD contact</>}{needs.length > 0 && blocked > 0 && ' · '}{blocked > 0 && <>{blocked} blocked by rules</>}
          </span>
        </summary>
        <div className="px-3 pb-3 space-y-3">
          {needs.length > 0 && <NeedsContactList items={needs} exportMeta={exportMeta} />}
          {Object.entries(groups).map(([reason, items]) => {
            const meta = REASON_META[reason] || { title: reason, color: 'slate', hint: '' };
            return (
              <div key={reason} className={`p-2.5 rounded-md border ${BORDER[meta.color] || BORDER.slate}`} data-testid={`skipped-group-${reason}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[11px] font-semibold text-slate-800">{meta.title}</span>
                  <span className="text-[10px] text-slate-500">({items.length})</span>
                </div>
                {meta.hint && <p className="text-[10px] text-slate-500 mb-1.5">{meta.hint}</p>}
                <div className="flex flex-wrap gap-1.5">
                  {items.map((it, idx) => (
                    <span key={`${it.company_name}-${idx}`} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-1 text-[11px] text-slate-700" title={it.reason_human || ''} data-testid={`skipped-item-${reason}-${idx}`}>
                      <span className="font-medium">{it.company_name}</span>
                      {it.country && <span className="text-[10px] text-slate-400">{it.country}</span>}
                      {it.action_url && it.action_label && (
                        <a href={it.action_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline text-[10px] font-medium">{it.action_label} →</a>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
};
